// 9fbef606107a605d69c0edbcd8029e5d
import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import isEmpty from 'lodash/isEmpty';
import DamImage from '@tcp/core/src/components/common/atoms/DamImage';
import Image from '@tcp/core/src/components/common/atoms/Image';
import { parseBoolean, getLocator, getImageFilePath } from '@tcp/core/src/utils';
import {
  StyledImageContainer,
  StyledImageBlock,
  StyleIconBlock,
} from '../styles/ImageAnimation.style';

const getPlayButton = (playing, togglePlay) => {
  const buttonClass = 'image_animation_play_pause_button';
  return playing ? (
    <button
      className={buttonClass}
      data-locator="imageAnimation_pause_button"
      onClick={togglePlay}
      aria-label="imageAnimation-pause-icon-button"
    >
      <Image
        className="image_animation_play_pause_button_icon"
        aria-hidden="true"
        src={`${getImageFilePath()}/pause-accessible.svg`}
      />
    </button>
  ) : (
    <button
      className={buttonClass}
      data-locator="imageAnimation_play_button"
      onClick={togglePlay}
      aria-label="imageAnimation-play-icon-button"
    >
      <Image
        className="image_animation_play_pause_button_icon"
        aria-hidden="true"
        src={`${getImageFilePath()}/play-accessible.svg`}
      />
    </button>
  );
};

const getImage = (item, index, isAutoPlayWithScrollEnabled, isPreload) => {
  const { image, singleCTAButton } = item;
  return (
    <DamImage
      imgData={image}
      dataLocator={`${getLocator('moduleContainer_img-animation')}${index + 1}`}
      className="module-container-img-full-width"
      link={singleCTAButton}
      isHomePage
      lazyLoad
      isModule
      noLink={isAutoPlayWithScrollEnabled}
      preloadImage={isPreload && (index === 0 || index === 1)}
    />
  );
};
// eslint-disable-next-line complexity, sonarjs/cognitive-complexity
const ImageAnimation = ({ imageData, animationSpeedProp, aspectRatioObj, isPreload, isHalfWidthModule }) => {
  const { image } = imageData ? imageData[0] : {};
  const { autoPlayWithScroll } = image;
  const containerRef = useRef(null);
  const sliderRef = useRef(null);
  const animationFlag = useRef(null);
  const [playing, setPlaying] = useState(true);
  const isAutoPlayWithScrollEnabled = autoPlayWithScroll && parseBoolean(autoPlayWithScroll);
  const animationVal = !isEmpty(animationSpeedProp) ? parseFloat(animationSpeedProp?.speed) : 0;
  const animationSpeed = animationVal;

  let alreadyScrollLeft = useRef(0).current;
  let startX = useRef(0).current;
  let pressed = useRef(false).current;

  const scroll = () => {
    if (containerRef.current && sliderRef.current) {
      if (
        Math.ceil(containerRef.current.scrollLeft) >=
        Math.ceil(sliderRef.current.offsetWidth - containerRef.current.offsetWidth)
      ) {
        containerRef.current.scrollLeft = Math.ceil(
          sliderRef.current.offsetWidth / 2 - containerRef.current.offsetWidth
        );
      } else {
        containerRef.current.scrollLeft =  Math.ceil(containerRef.current.scrollLeft + animationSpeed);
      }
      animationFlag.current = requestAnimationFrame(scroll);
    }
  };

  useEffect(() => {
    if (containerRef.current && sliderRef.current) {
      scroll();
    }
    return () => cancelAnimationFrame(animationFlag.current);
  }, []);

  const togglePlay = () => {
    if (playing === true) {
      setPlaying(false);
      cancelAnimationFrame(animationFlag.current);
    } else {
      setPlaying(true);
      animationFlag.current = requestAnimationFrame(scroll);
    }
  };

  const handleMouseDown = (event) => {
    if (containerRef.current) {
      pressed = true;
      startX = event.pageX - containerRef.current.offsetLeft;
      alreadyScrollLeft = containerRef.current.scrollLeft;
      containerRef.current.style.cursor = 'grabbing';
      cancelAnimationFrame(animationFlag.current);
    }
  };

  const handleMouseLeave = () => {
    if (containerRef.current) {
      pressed = false;
      containerRef.current.style.cursor = 'default';
    }
  };

  const handleMouseUp = () => {
    if (containerRef.current) {
      pressed = false;
      containerRef.current.style.cursor = 'grab';
      if (playing === true) {
        requestAnimationFrame(scroll);
      }
    }
  };

  const handleMouseEnter = () => {
    if (containerRef.current) containerRef.current.style.cursor = 'grab';
  };

  const handleMouseMove = (event) => {
    event.preventDefault();
    if (!pressed) return;
    if (isAutoPlayWithScrollEnabled && containerRef.current) {
      containerRef.current.style.cursor = 'grabbing';
      event.preventDefault();
      const currentX = event.pageX - containerRef.current.offsetLeft;
      const scrollDistance = (currentX - startX) * 1.5;
      containerRef.current.scrollLeft = alreadyScrollLeft - scrollDistance;
      if (
        containerRef.current.scrollLeft >=
        sliderRef.current.offsetWidth - containerRef.current.offsetWidth
      ) {
        containerRef.current.scrollLeft =
          sliderRef.current.offsetWidth / 2 - containerRef.current.offsetWidth;
      } else if (containerRef.current.scrollLeft <= 0) {
        containerRef.current.scrollLeft = sliderRef.current.offsetWidth / 2;
      }
    }
  };

  const handleTouchStart = (event) => {
    startX = event.touches[0].pageX - containerRef.current.offsetLeft;
    alreadyScrollLeft = containerRef.current.scrollLeft;
  };

  const handleTouchMove = (event) => {
    if (!isAutoPlayWithScrollEnabled) return;
    const currentX = event.touches[0].pageX - containerRef.current.offsetLeft;
    const scrollDistance = (currentX - startX) * 1.5;
    containerRef.current.scrollLeft = alreadyScrollLeft - scrollDistance;
    if (
      containerRef.current.scrollLeft >=
      sliderRef.current.offsetWidth - containerRef.current.offsetWidth
    ) {
      containerRef.current.scrollLeft =
        sliderRef.current.offsetWidth / 2 - containerRef.current.offsetWidth;
    } else if (containerRef.current.scrollLeft <= 0) {
      containerRef.current.scrollLeft = sliderRef.current.offsetWidth / 2;
    }
  };

  const cloneElement = () => {
    const imgElm = imageData?.map((item, index) =>
      getImage(item, index, isAutoPlayWithScrollEnabled, isPreload)
    );
    return [...imgElm, ...imgElm];
  };

  return (
    <>
      <StyledImageContainer
        ref={containerRef}
        onMouseDown={handleMouseDown}
        onMouseLeave={handleMouseLeave}
        onMouseUp={handleMouseUp}
        onMouseEnter={handleMouseEnter}
        onMouseMove={handleMouseMove}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        aspectRatioObj={aspectRatioObj}
        isHalfWidthModule={isHalfWidthModule}
      >
        <StyledImageBlock ref={sliderRef} isHalfWidthModule={isHalfWidthModule}>{cloneElement()}</StyledImageBlock>
      </StyledImageContainer>
      <StyleIconBlock className="image_animation_icon_block" isHalfWidthModule={isHalfWidthModule}>
          {getPlayButton(playing, togglePlay)}
        </StyleIconBlock>
    </>
  );
};

ImageAnimation.propTypes = {
  imageData: PropTypes.arrayOf(
    PropTypes.shape({
      image: PropTypes.shape({
        url: PropTypes.string,
      }),
    })
  ).isRequired,
  animationSpeedProp: PropTypes.number.isRequired,
  aspectRatioObj: PropTypes.shape({}).isRequired,
  isPreload: PropTypes.bool,
  isHalfWidthModule: PropTypes.bool,
};

ImageAnimation.defaultProps = {
  isPreload: false,
  isHalfWidthModule: false
};

export default ImageAnimation;
export { ImageAnimation as ImageAnimationVanilla };
